import { Button } from "../ui/button";
import { StatusBadge } from "./StatusBadge";
import { AudioPlayer } from "./AudioPlayer";
import { User, Loader2, RefreshCw, Music, Video, Volume2 } from "lucide-react";

export function AvatarSection({
  segment, title, description, iconColor, script,
  audioData, videoData, compositeReady, audioReady,
  generating, onGenerateAudio, onGenerateVideo
}) {
  const videoReady = videoData.video_status === 'ready';
  const bgColor = segment === 'intro' ? 'bg-[#4D11F6]/10' : 'bg-emerald-50';
  const iconColorClass = segment === 'intro' ? 'text-[#4D11F6]' : 'text-emerald-600';

  return (
    <section className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden" data-testid={`production-avatar-${segment}`}>
      <div className="px-5 py-4 border-b border-[#E2E4E7] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center`}>
            <User size={18} className={iconColorClass} />
          </div>
          <div>
            <h3 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>{title}</h3>
            <p className="text-xs text-[#94969C]">{description}</p>
          </div>
        </div>
        <StatusBadge status={videoReady ? 'ready' : videoData.video_status || null} />
      </div>
      <div className="p-5 space-y-4">
        <div className="bg-[#F3F3F9] rounded-lg p-3">
          <p className="text-xs text-[#94969C] uppercase tracking-wide mb-1 font-medium">{segment === 'intro' ? 'Intro Script' : 'CTA Script'}</p>
          <p className="text-sm text-[#1A2238]">{script || 'No script generated yet'}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {/* Audio */}
          <div className="p-3 rounded-lg border border-[#E2E4E7] text-center space-y-2">
            <div className="w-full aspect-video rounded bg-[#F3F3F9] flex items-center justify-center border border-[#E2E4E7]">
              {audioReady ? <AudioPlayer url={audioData.url} /> : <Music size={16} className="text-[#D8D9DD]" />}
            </div>
            <p className="text-xs font-medium text-[#1A2238]">Audio</p>
            <StatusBadge status={audioData.status} />
            <Button size="sm" onClick={onGenerateAudio}
              disabled={generating.audio}
              data-testid={`gen-${segment}-audio-btn`}
              className="text-xs bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-6 w-full">
              {generating.audio ? <><Loader2 size={10} className="animate-spin" /> Generating...</> : audioData.status === 'failed' ? <><RefreshCw size={10} /> Retry</> : audioReady ? <><RefreshCw size={10} /> Regenerate</> : <><Volume2 size={10} /> Generate</>}
            </Button>
          </div>
          {/* Video */}
          <div className="p-3 rounded-lg border border-[#E2E4E7] text-center space-y-2">
            <div className="w-full aspect-video rounded bg-[#F3F3F9] overflow-hidden border border-[#E2E4E7]">
              {videoData.video_url ? (
                <video src={videoData.video_url} className="w-full h-full object-cover" controls muted />
              ) : videoData.video_status === 'processing' ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                  <Loader2 size={16} className="text-[#4D11F6] animate-spin" />
                  <span className="text-[10px] text-[#94969C]">HeyGen rendering...</span>
                </div>
              ) : <div className="w-full h-full flex items-center justify-center"><Video size={16} className="text-[#D8D9DD]" /></div>}
            </div>
            <p className="text-xs font-medium text-[#1A2238]">Video</p>
            <StatusBadge status={videoData.video_status} />
            {videoData.video_error && videoData.video_status === 'failed' && <p className="text-[10px] text-red-600 line-clamp-2">{videoData.video_error}</p>}
            {(!compositeReady || !audioReady) && !videoData.video_status && <p className="text-[10px] text-amber-600">{!compositeReady ? 'Needs agent composite' : 'Needs audio'}</p>}
            <Button size="sm" onClick={onGenerateVideo}
              disabled={generating.video || !compositeReady || !audioReady}
              data-testid={`gen-${segment}-video-btn`}
              className="text-xs bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-6 w-full">
              {generating.video ? <Loader2 size={10} className="animate-spin" /> : videoReady ? <RefreshCw size={10} /> : <Video size={10} />}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
